import { useEffect, useMemo, useState } from "react";
import { isTevoNumericEventId } from "./marketplaceHomeFeaturedFromApi";
import { fetchTevoListingPrice } from "./marketplaceHotEventPrices";

export function applyListingPrices(cards, listingPrices) {
  return cards.map((card) => {
    const key = card?.eventId != null ? String(card.eventId) : "";
    const fetched = key ? listingPrices[key] : 0;
    if (fetched > 0) return { ...card, price: fetched };
    return card;
  });
}

/** Hero carousel slides use `startingPrice` instead of `price`. */
export function applyStartingPrices(slides, listingPrices) {
  return slides.map((slide) => {
    const key = slide?.eventId != null ? String(slide.eventId) : "";
    const fetched = key ? listingPrices[key] : 0;
    if (fetched > 0) return { ...slide, startingPrice: fetched };
    return slide;
  });
}

function readCardPrice(card) {
  if (card?.price != null && card.price > 0) return card.price;
  if (card?.startingPrice != null && card.startingPrice > 0) return card.startingPrice;
  return 0;
}

/**
 * Batch-fetch TEVO listing prices (lowest ticket_groups retail) for cards/slides
 * that still show $0 after resolveListingPrice.
 */
export function useBatchListingPrices(cards, { requireFromApi = false } = {}) {
  const [listingPrices, setListingPrices] = useState({});

  const cardsNeedingFetch = useMemo(() => {
    const byId = new Map();
    (cards || []).forEach((card) => {
      if (!card?.eventId) return;
      if (requireFromApi && !card._fromApi) return;
      if (readCardPrice(card) > 0) return;
      byId.set(String(card.eventId), card);
    });
    return [...byId.values()];
  }, [cards, requireFromApi]);

  const fetchKey = useMemo(
    () => cardsNeedingFetch.map((c) => String(c.eventId)).sort().join(","),
    [cardsNeedingFetch]
  );

  useEffect(() => {
    if (!cardsNeedingFetch.length) return undefined;

    let cancelled = false;

    const loadPrices = async () => {
      const updates = {};
      await Promise.all(
        cardsNeedingFetch.map(async (card) => {
          if (!isTevoNumericEventId(card.eventId)) return;
          const price = await fetchTevoListingPrice(card.eventId);
          if (price > 0) updates[String(card.eventId)] = price;
        })
      );
      if (!cancelled && Object.keys(updates).length > 0) {
        setListingPrices((prev) => ({ ...prev, ...updates }));
      }
    };

    loadPrices();
    return () => {
      cancelled = true;
    };
  }, [fetchKey, cardsNeedingFetch]);

  return listingPrices;
}

/** Fetch TEVO listing prices for discover cards (hot + trending) that still show $0. */
export function useDiscoverListingPrices(hotCards, trendingCards) {
  const merged = useMemo(
    () => [...(hotCards || []), ...(trendingCards || [])],
    [hotCards, trendingCards]
  );
  return useBatchListingPrices(merged, { requireFromApi: true });
}
